import React from 'react';
import { Modal } from 'antd';
import styles from './asset/privacyPolicyModal.module.scss';

const PrivacyPolicyModal = ({ isOpen, onClose }) => {
    return (
        <Modal
            open={isOpen}
            onCancel={onClose}
            footer={null}
            width={800}
            centered
            className={styles.privacyModal}
        >
            <div className={styles.modalContent}>
                <h1 className={styles.title}>Privacy Policy</h1>
                <p className={styles.lastUpdated}>Last Updated: November 4, 2025</p>

                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>1. Introduction</h2>
                    <p className={styles.text}>
                        At Prepal, we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your data when you use our AI-powered test preparation platform.
                    </p>
                </div>

                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>2. Information We Collect</h2>
                    <p className={styles.text}>
                        We collect several types of information to provide and improve our services:
                    </p>
                    <h3 className={styles.subsectionTitle}>Personal Information</h3>
                    <ul className={styles.list}>
                        <li>Name and email address</li>
                        <li>Account credentials</li>
                        <li>Payment and billing information</li>
                        <li>Profile information you choose to provide</li>
                    </ul>
                    <h3 className={styles.subsectionTitle}>Usage Information</h3>
                    <ul className={styles.list}>
                        <li>Test performance and practice activity</li>
                        <li>Learning patterns and progress data</li>
                        <li>Device information and IP address</li>
                        <li>Browser type and operating system</li>
                    </ul>
                </div>

                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>3. How We Use Your Information</h2>
                    <p className={styles.text}>
                        We use the collected information for the following purposes:
                    </p>
                    <ul className={styles.list}>
                        <li>Provide personalized test preparation and learning recommendations</li>
                        <li>Process payments and manage your subscription</li>
                        <li>Analyze and improve our AI algorithms and platform features</li>
                        <li>Send important updates, notifications, and educational content</li>
                        <li>Provide customer support and respond to inquiries</li>
                        <li>Ensure platform security and prevent fraud</li>
                    </ul>
                </div>

                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>4. Data Sharing and Disclosure</h2>
                    <p className={styles.text}>
                        We do not sell your personal information. We may share your data only in the following circumstances:
                    </p>
                    <ul className={styles.list}>
                        <li><strong>Service Providers:</strong> Third-party vendors who help us operate our platform (payment processors, cloud hosting, analytics)</li>
                        <li><strong>Legal Requirements:</strong> When required by law or to protect our rights and safety</li>
                        <li><strong>Business Transfers:</strong> In connection with mergers, acquisitions, or asset sales</li>
                        <li><strong>With Your Consent:</strong> When you explicitly authorize us to share your information</li>
                    </ul>
                </div>

                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>5. Data Security</h2>
                    <p className={styles.text}>
                        We implement industry-standard security measures to protect your personal information, including:
                    </p>
                    <ul className={styles.list}>
                        <li>Encryption of data in transit and at rest</li>
                        <li>Regular security audits and vulnerability assessments</li>
                        <li>Secure authentication and access controls</li>
                        <li>Employee training on data protection practices</li>
                    </ul>
                    <p className={styles.text}>
                        However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
                    </p>
                </div>

                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>6. Your Rights and Choices</h2>
                    <p className={styles.text}>
                        You have the following rights regarding your personal information:
                    </p>
                    <ul className={styles.list}>
                        <li><strong>Access:</strong> Request a copy of your personal data</li>
                        <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                        <li><strong>Deletion:</strong> Request deletion of your account and data</li>
                        <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications</li>
                        <li><strong>Data Portability:</strong> Receive your data in a structured format</li>
                    </ul>
                    <p className={styles.text}>
                        To exercise these rights, please contact us at privacy@prepal.com.
                    </p>
                </div>

                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>7. Cookies and Tracking Technologies</h2>
                    <p className={styles.text}>
                        We use cookies and similar tracking technologies to enhance your experience, analyze usage patterns, and deliver personalized content. You can control cookie preferences through your browser settings.
                    </p>
                </div>

                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>8. Children's Privacy</h2>
                    <p className={styles.text}>
                        Prepal is designed for users aged 13 and older. We do not knowingly collect personal information from children under 13. If we become aware of such collection, we will take steps to delete the information.
                    </p>
                </div>

                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>9. International Data Transfers</h2>
                    <p className={styles.text}>
                        Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place to protect your data in accordance with this Privacy Policy.
                    </p>
                </div>

                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>10. Changes to This Policy</h2>
                    <p className={styles.text}>
                        We may update this Privacy Policy from time to time. We will notify you of significant changes via email or through our platform. Your continued use of Prepal after changes constitutes acceptance of the updated policy.
                    </p>
                </div>

                <div className={styles.section}>
                    <h2 className={styles.sectionTitle}>11. Contact Us</h2>
                    <p className={styles.text}>
                        If you have questions or concerns about this Privacy Policy or our data practices, please contact us:
                    </p>
                    <p className={styles.contactInfo}>
                        Email: privacy@prepal.com<br />
                        Data Protection Officer: dpo@prepal.com<br />
                        Address: Prepal Inc., Privacy Department
                    </p>
                </div>

                <div className={styles.footer}>
                    <button onClick={onClose} className={styles.closeButton}>
                        Close
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default PrivacyPolicyModal;
